// Central financial calculation engine for the Investment Feasibility Report.
// Pure functions — no React, no side effects. Every KPI, chart and scenario
// in the UI reads from this single module.
// ---------------------------------------------------------------------------

export const BASE_PRICES = {
  cs37: 1990000, cs33: 1870000, cs31: 1750000, cs25: 1300000,
  ss35: 1970000, ss32: 1710000, ss26: 1370000,
};
export const FIT_OUT_PCT = 0.40;
export const MAINT_PCT_DEFAULT = 0.05;
export const OPEX_PCT_DEFAULT = 0.10;    // extra operating costs beyond maintenance
export const OPERATING_DAYS = 365;

// ---------- EMI ----------
export function emiMonthly({ finance_amount, interest_rate, finance_tenure_years }) {
  const P = Number(finance_amount || 0);
  const r = Number(interest_rate || 0) / 100 / 12;
  const n = Number(finance_tenure_years || 0) * 12;
  if (P <= 0 || r <= 0 || n <= 0) return 0;
  const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  return Number.isFinite(emi) ? emi : 0;
}

// ---------- IRR via Newton–Raphson ----------
export function irr(cashflows, guess = 0.15) {
  const npv = (rate) => cashflows.reduce((s, cf, i) => s + cf / Math.pow(1 + rate, i), 0);
  const dnpv = (rate) => cashflows.reduce((s, cf, i) => s - (i * cf) / Math.pow(1 + rate, i + 1), 0);
  let r = guess;
  for (let i = 0; i < 60; i++) {
    const f = npv(r);
    const fp = dnpv(r);
    if (!Number.isFinite(f) || !Number.isFinite(fp) || fp === 0) break;
    const rn = r - f / fp;
    if (Math.abs(rn - r) < 1e-7) { r = rn; break; }
    r = rn;
    if (r < -0.99) return null;
  }
  return Number.isFinite(r) ? r * 100 : null;
}

// ---------- Core project metrics ----------
export function computeMetrics(input) {
  const {
    model_slug = 'cs33', units = 5, adr = 15000, occupancy = 65,
    land_ownership = 'looking', financing_plan = 'self',
    finance_amount = 0, interest_rate = 0, finance_tenure_years = 0, down_payment_pct = 0,
    monthly_lease = 0, security_deposit = 0,
    include_land_appreciation = true, annual_land_appreciation = 6, annual_building_depreciation = 1.5,
    land_value = 5000000,
    maint_pct = MAINT_PCT_DEFAULT, opex_pct = OPEX_PCT_DEFAULT,
    horizon_years = 10,
    site_development_cost = 0,   // ← user-entered, ₹0 by default. Project-specific capital cost.
  } = input;

  const basePrice = BASE_PRICES[model_slug] || 0;
  const modulesTotal = basePrice * units;                    // Modularoom units (product only)
  const fitOutTotal  = modulesTotal * FIT_OUT_PCT;           // Fit-out on top of modules
  const siteDevTotal = Math.max(0, Number(site_development_cost) || 0);
  const capex        = modulesTotal + fitOutTotal + siteDevTotal;

  const isLease = land_ownership === 'lease';
  const isOwned = land_ownership === 'owned';
  const totalInvestment = capex + (isLease ? Number(security_deposit || 0) : 0);
  // Equity actually deployed by the investor (down payment + lease deposit)
  const equityDeployed = capex * ((financing_plan === 'finance' || financing_plan === 'mixed')
    ? Math.max(0, Number(down_payment_pct || 0)) / 100 : 1)
    + (isLease ? Number(security_deposit || 0) : 0);

  const occ = Math.max(0, Math.min(100, Number(occupancy))) / 100;
  const annualRevenue = Number(adr) * occ * OPERATING_DAYS * Number(units);
  const monthlyRevenue = annualRevenue / 12;

  const annualMaint = annualRevenue * maint_pct;
  const annualOpex  = annualRevenue * opex_pct;
  const annualLease = isLease ? (Number(monthly_lease) * 12) : 0;
  const annualEmi   = (financing_plan === 'finance' || financing_plan === 'mixed') ? emiMonthly(input) * 12 : 0;

  const annualProfit = annualRevenue - annualMaint - annualOpex - annualLease - annualEmi;
  const monthlyCashFlow = annualProfit / 12;

  const roi = totalInvestment > 0 ? (annualProfit / totalInvestment) * 100 : 0;
  const cashOnCash = equityDeployed > 0 ? (annualProfit / equityDeployed) * 100 : 0;
  const payback = annualProfit > 0 ? totalInvestment / annualProfit : Infinity;

  // Wealth after horizon
  const landAppr = (isOwned && include_land_appreciation)
    ? Number(land_value) * Math.pow(1 + Number(annual_land_appreciation) / 100, horizon_years)
    : 0;
  const bldgVal = capex * Math.pow(1 - Number(annual_building_depreciation) / 100, horizon_years);
  const cumProfit = annualProfit * horizon_years;
  const wealth = cumProfit + landAppr + bldgVal;

  // Cash-flow stream for IRR: year 0 outflow = totalInvestment, years 1..N inflow = annualProfit,
  // terminal-year inflow adds residual asset value (land + building).
  const cashflows = [-totalInvestment];
  for (let y = 1; y <= horizon_years; y++) {
    const inflow = annualProfit + (y === horizon_years ? (landAppr + bldgVal) : 0);
    cashflows.push(inflow);
  }
  const irrPct = irr(cashflows);

  return {
    modulesTotal, fitOutTotal, siteDevTotal,
    capex, totalInvestment, equityDeployed,
    monthlyRevenue, annualRevenue, annualMaint, annualOpex, annualLease, annualEmi, annualProfit, monthlyCashFlow,
    roi, cashOnCash, payback, irrPct,
    landAppr, bldgVal, cumProfit, wealth,
    horizon_years,
  };
}

// ---------- Three-scenario runner ----------
// Adjusts occupancy while keeping every other assumption constant.
export function runScenarios(baseInput) {
  const occ = Number(baseInput.occupancy) || 60;
  const conservative = Math.max(20, Math.round(occ * 0.75));
  const optimistic = Math.min(95, Math.round(occ + (100 - occ) * 0.5));
  const runs = [
    { key: 'conservative', label: 'Conservative', occupancy: conservative },
    { key: 'expected',     label: 'Expected',     occupancy: occ },
    { key: 'optimistic',   label: 'Optimistic',   occupancy: optimistic },
  ];
  return runs.map((r) => ({ ...r, metrics: computeMetrics({ ...baseInput, occupancy: r.occupancy }) }));
}

// ---------- Benchmark investments (see /app/frontend/src/data/benchmarks.js) ----------
// Growth: annual %, compounded on the same initial investment as the MODULAROOM project.
export function benchmarkWealth(initialInvestment, annualPct, years = 10) {
  const P = Number(initialInvestment) || 0;
  const r = Number(annualPct) || 0;
  return P * Math.pow(1 + r / 100, years);
}
